/**
 * Mirrors the `task-attachments` bucket's mime allowlist (see the
 * `20260807160000_task_attachments` migration). Checking it client-side
 * means a disallowed file gets a clear, immediate reason instead of a bare
 * "upload failed (400)" once Storage rejects the PUT.
 *
 * html, svg and javascript aren't on the bucket's allowlist and never will
 * be: the bucket is public-read, so storing one under its real content type
 * would make it a live, executable page at that URL. Rather than refusing
 * those outright, they're re-tagged to `application/octet-stream` before
 * upload — see `effectiveContentType` below — which is already allowed and
 * inert: a browser opening the public URL downloads the bytes instead of
 * rendering or running them, whatever they actually contain. The file still
 * gets attached; it just never becomes a page.
 *
 * Kept in its own module, apart from the rest of uploads.ts, so it can be
 * unit-tested without pulling in the Supabase client.
 */
const ALLOWED_ATTACHMENT_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/avif',
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/x-log',
  'application/json',
  'application/x-yaml',
  'text/yaml',
  'application/zip',
  'application/x-tar',
  'application/gzip',
  'application/x-gzip',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/octet-stream',
]);

const SAFE_DOWNLOAD_TYPE = 'application/octet-stream';

/** Types a browser will render or execute if served under their real content type from a public URL. */
const FORCE_DOWNLOAD_TYPES = new Set([
  'text/html',
  'application/xhtml+xml',
  'image/svg+xml',
  'text/javascript',
  'application/javascript',
]);

/**
 * The content type to actually upload with. Ordinary types pass through
 * unchanged; a type a browser would render or execute from a public URL —
 * html, svg, javascript — is swapped for an inert one so the upload
 * succeeds without the file ever running as a live page. Apply this before
 * `isAllowedAttachmentType` and before building the upload request.
 */
export function effectiveContentType(contentType: string): string {
  return FORCE_DOWNLOAD_TYPES.has(contentType) ? SAFE_DOWNLOAD_TYPE : contentType;
}

export function isAllowedAttachmentType(contentType: string): boolean {
  return ALLOWED_ATTACHMENT_TYPES.has(effectiveContentType(contentType));
}

export function rejectionReason(contentType: string): string {
  return `${contentType || 'this file type'} isn't supported here.`;
}
